
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Building2, Database, ShieldCheck, UserRound,
  MapPin, FileText, Save, Loader2, RefreshCw, Ban,
  CheckCircle2, Clock3, AlertCircle, Info, History, Play
} from "lucide-react";

import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";

const API_BASE = (
  process.env.REACT_APP_API_URL || "https://supremeadmin.nissi-india.com"
).replace(/\/+$/, "");

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 30000
});

const DB_TYPES = ["MASTER", "INVENTORY", "PAYROLL", "TALLY"];

const EMPTY_COMPANY = {
  name: "", legalName: "", contactPerson: "", email: "",
  phone: "", alternatePhone: "", website: "", domain: "",
  addressLine1: "", addressLine2: "", city: "",
  stateName: "", postalCode: "", country: "", gstin: "", pan: ""
};

const EMPTY_LICENSE = {
  planCode: "STARTER", maxUsers: 5, expiresOn: "",
  graceDays: 7, isSuspended: true, featuresJson: "{}"
};

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Request failed.";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString();
};

const formatDateInput = (value) => {
  if (!value) return "";
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0")
  ].join("-");
};

function Section({ title, icon: Icon, children, action }) {
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-emerald-50 px-5 py-3">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-emerald-700" />
          <h2 className="text-sm font-bold text-slate-800">{title}</h2>
        </div>
        {action}
      </div>
      <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
        {children}
      </div>
    </section>
  );
}

function Field({
  label, value, onChange, type = "text",
  disabled = false, required = false, maxLength, min, max
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="ml-1 text-rose-500">*</span>}
      </span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        maxLength={maxLength}
        min={min}
        max={max}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 disabled:bg-slate-100 disabled:text-slate-500"
      />
    </label>
  );
}

function StatusBadge({ status }) {
  const value = String(status || "UNKNOWN").toUpperCase();
  const colors = {
    READY: "border-emerald-200 bg-emerald-50 text-emerald-700",
    COMPLETED: "border-emerald-200 bg-emerald-50 text-emerald-700",
    PENDING: "border-amber-200 bg-amber-50 text-amber-700",
    RUNNING: "border-blue-200 bg-blue-50 text-blue-700",
    FAILED: "border-rose-200 bg-rose-50 text-rose-700"
  };
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
      colors[value] || "border-slate-200 bg-slate-100 text-slate-600"
    }`}>
      {value}
    </span>
  );
}

function JobStatusBadge({ status }) {
  const labels = {
    0: "PENDING", 1: "RUNNING",
    2: "COMPLETED", 3: "FAILED"
  };
  return <StatusBadge status={labels[Number(status)] || "UNKNOWN"} />;
}

function Notice({ type = "info", children }) {
  const styles = {
    info: "border-blue-200 bg-blue-50 text-blue-800",
    warning: "border-amber-200 bg-amber-50 text-amber-800",
    error: "border-rose-200 bg-rose-50 text-rose-800",
    success: "border-emerald-200 bg-emerald-50 text-emerald-800"
  };
  const Icon = type === "success" ? CheckCircle2
    : type === "error" ? AlertCircle
    : type === "warning" ? Clock3 : Info;

  return (
    <div className={`flex gap-2 rounded-lg border p-3 text-sm ${styles[type]}`}>
      <Icon size={18} className="mt-0.5 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

export default function TenantEdit() {
  const { tenantId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingLicense, setSavingLicense] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState("");

  const [tenant, setTenant] = useState(null);
  const [company, setCompany] = useState(EMPTY_COMPANY);
  const [license, setLicense] = useState(EMPTY_LICENSE);
  const [databases, setDatabases] = useState([]);

  const [provisioningJobs, setProvisioningJobs] = useState([]);
  const [provisioningLoading, setProvisioningLoading] = useState(false);
  const [provisioningError, setProvisioningError] = useState("");
  const [provisioningLoaded, setProvisioningLoaded] = useState(false);
  const [provisioningEnabled, setProvisioningEnabled] = useState(false);
  const [templatesApproved, setTemplatesApproved] = useState(false);
  const [serverProvisioningRunning, setServerProvisioningRunning] = useState(false);
  const [startingProvisioning, setStartingProvisioning] = useState(false);
  const [provisioningResult, setProvisioningResult] = useState("");

  const requestRunningRef = useRef(false);
  const statusLoadingRef = useRef(false);

  const setCompanyField = (key, value) =>
    setCompany((previous) => ({ ...previous, [key]: value }));

  const setLicenseField = (key, value) =>
    setLicense((previous) => ({ ...previous, [key]: value }));

  const loadProvisioningStatus = useCallback(async (silent = false) => {
    if (statusLoadingRef.current) return null;
    statusLoadingRef.current = true;

    if (!silent) setProvisioningLoading(true);

    try {
      const response = await api.get(
        `/api/tenants/${tenantId}/provisioning`,
        { timeout: 60000 }
      );

      const data = response.data;

      if (data?.success === false || data?.ok === false) {
        throw new Error(data?.message || "Unable to fetch provisioning status.");
      }

      if (!Array.isArray(data?.databases)) {
        throw new Error("Invalid provisioning response.");
      }

      setDatabases(data.databases);
      setProvisioningJobs(Array.isArray(data.jobs) ? data.jobs : []);
      setProvisioningEnabled(data.provisioningEnabled === true);
      setTemplatesApproved(data.templatesApproved === true);
      setServerProvisioningRunning(data.provisioningRunning === true);
      setProvisioningLoaded(true);
      setProvisioningError("");

      return data;
    } catch (err) {
      if (!silent) {
        setProvisioningError(getErrorMessage(err));
      }
      return null;
    } finally {
      statusLoadingRef.current = false;
      if (!silent) setProvisioningLoading(false);
    }
  }, [tenantId]);

  const loadDetails = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get(
        `/api/tenants/${tenantId}/infra-details`
      );
      const data = response.data;

      if (data?.success === false || !data?.company) {
        throw new Error(data?.message || "Tenant details not found.");
      }

      const item = data.company;
      setTenant(item);

      setCompany({
        name: item.Name || "",
        legalName: item.LegalName || "",
        contactPerson: item.ContactPerson || "",
        email: item.Email || "",
        phone: item.Phone || "",
        alternatePhone: item.AlternatePhone || "",
        website: item.Website || "",
        domain: item.Domain || "",
        addressLine1: item.AddressLine1 || "",
        addressLine2: item.AddressLine2 || "",
        city: item.City || "",
        stateName: item.StateName || "",
        postalCode: item.PostalCode || "",
        country: item.Country || "",
        gstin: item.GSTIN || "",
        pan: item.PAN || ""
      });

      setLicense({
        planCode: item.PlanCode || "STARTER",
        maxUsers: item.MaxUsers ?? 5,
        expiresOn: formatDateInput(item.ExpiresOn),
        graceDays: item.GraceDays ?? 7,
        isSuspended: item.IsSuspended === true || item.IsSuspended === 1,
        featuresJson: item.FeaturesJson || "{}"
      });

      setDatabases(Array.isArray(data.databases) ? data.databases : []);
      await loadProvisioningStatus();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [tenantId, loadProvisioningStatus]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  // Poll the job history while provisioning is running.
  useEffect(() => {
    if (!startingProvisioning && !serverProvisioningRunning) return;

    const timer = setInterval(() => {
      loadProvisioningStatus(true);
    }, 5000);

    return () => clearInterval(timer);
  }, [startingProvisioning, serverProvisioningRunning, loadProvisioningStatus]);

  const allDatabasesReady =
    provisioningLoaded &&
    databases.length === 4 &&
    DB_TYPES.every((type) =>
      databases.some(
        (db) =>
          db.DatabaseType === type &&
          String(db.ProvisioningStatus).toUpperCase() === "READY"
      )
    );

  const readyCount = databases.filter(
    (db) => String(db.ProvisioningStatus).toUpperCase() === "READY"
  ).length;

  const runningCount = databases.filter(
    (db) => String(db.ProvisioningStatus).toUpperCase() === "RUNNING"
  ).length;

  const failedCount = databases.filter(
    (db) => String(db.ProvisioningStatus).toUpperCase() === "FAILED"
  ).length;

  const isProvisioningBusy =
    startingProvisioning ||
    serverProvisioningRunning ||
    runningCount > 0;

  const canStartProvisioning =
    provisioningLoaded &&
    provisioningEnabled &&
    templatesApproved &&
    databases.length === 4 &&
    !allDatabasesReady &&
    !isProvisioningBusy &&
    !provisioningLoading;

  // ==========================================================
  // START PROVISIONING
  // POST /api/tenants/:tenantId/provision
  // ==========================================================

  const handleStartProvisioning = async () => {
    if (requestRunningRef.current || !canStartProvisioning) return;

    const confirmation = await Swal.fire({
      icon: "warning",
      title: "Start Database Provisioning?",
      html: `
        <div style="text-align:left;font-size:14px;line-height:1.8">
          <p>This will restore the tenant databases from approved templates:</p>
          <ul style="margin:10px 0;padding-left:20px;list-style:disc">
            <li>MASTER</li>
            <li>INVENTORY</li>
            <li>PAYROLL</li>
            <li>TALLY</li>
          </ul>
          <p><b>Existing databases will not be overwritten.</b></p>
          <p>The process may take several minutes. The tenant will remain inactive.</p>
          <p>Proceed only after backup sanitization, integrity checks and disk-space verification.</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Start Provisioning",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#059669",
      reverseButtons: true
    });

    if (!confirmation.isConfirmed || requestRunningRef.current) return;

    requestRunningRef.current = true;
    setStartingProvisioning(true);
    setProvisioningResult("");
    setProvisioningError("");

    try {
      // Recheck the server immediately before starting.
      const status = await loadProvisioningStatus();

      if (!status) throw new Error("Unable to verify provisioning status.");

      if (
        status.provisioningEnabled !== true ||
        status.templatesApproved !== true
      ) {
        throw new Error(
          "Provisioning is not enabled or the templates are not approved."
        );
      }

      if (
        status.provisioningRunning === true ||
        status.databases.some(
          (db) => String(db.ProvisioningStatus).toUpperCase() === "RUNNING"
        )
      ) {
        throw new Error("Provisioning is already running.");
      }

      const response = await api.post(
        `/api/tenants/${tenantId}/provision`,
        {},
        {
          // Restore runs inside the current backend request.
          // No Axios timeout; proxy/server timeouts still apply.
          timeout: 0
        }
      );

      if (response.data?.success === false) {
        throw new Error(
          response.data?.message || "Database provisioning failed."
        );
      }

      setProvisioningResult(
        response.data?.message ||
        "Database provisioning completed successfully."
      );

      await loadProvisioningStatus();

      await Swal.fire({
        icon: "success",
        title: "Provisioning Completed",
        text: response.data?.message ||
          "All four databases have been restored.",
        confirmButtonColor: "#059669"
      });
    } catch (err) {
      const message = getErrorMessage(err);
      setProvisioningError(message);

      await Swal.fire({
        icon: "error",
        title: "Provisioning Not Completed",
        text: `${message} Check the provisioning job history before retrying.`,
        confirmButtonColor: "#059669"
      });

      await loadProvisioningStatus();
    } finally {
      requestRunningRef.current = false;
      setStartingProvisioning(false);
      await loadProvisioningStatus();
    }
  };

  // ==========================================================
  // UPDATE COMPANY
  // ==========================================================

  const handleCompanySave = async (event) => {
    event.preventDefault();
    if (savingCompany) return;

    if (!company.name.trim() || !company.legalName.trim()) {
      return Swal.fire(
        "Validation",
        "Company name and legal name are required.",
        "warning"
      );
    }

    setSavingCompany(true);

    try {
      const response = await api.put(
        `/api/tenants/${tenantId}/infra-company`,
        company
      );

      if (response.data?.success === false || response.data?.ok === false) {
        throw new Error(response.data?.message || "Company update failed.");
      }

      await Swal.fire({
        icon: "success",
        title: "Company Updated",
        text: "Company information saved successfully.",
        confirmButtonColor: "#059669"
      });

      await loadDetails();
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: getErrorMessage(err),
        confirmButtonColor: "#059669"
      });
    } finally {
      setSavingCompany(false);
    }
  };

  // ==========================================================
  // UPDATE LICENSE
  // ==========================================================

  const handleLicenseSave = async (event) => {
    event.preventDefault();
    if (savingLicense || isProvisioningBusy) return;

    if (
      !license.planCode ||
      !license.expiresOn ||
      !Number.isInteger(Number(license.maxUsers)) ||
      Number(license.maxUsers) < 1 ||
      !Number.isInteger(Number(license.graceDays)) ||
      Number(license.graceDays) < 0 ||
      Number(license.graceDays) > 3650
    ) {
      return Swal.fire("Validation", "Enter valid license details.", "warning");
    }

    try {
      if (license.featuresJson.trim()) JSON.parse(license.featuresJson);
    } catch {
      return Swal.fire("Invalid JSON", "Please correct Features JSON.", "warning");
    }

    if (!license.isSuspended) {
      const status = await loadProvisioningStatus();

      if (
        !status ||
        status.allReady !== true ||
        status.provisioningRunning === true
      ) {
        return Swal.fire({
          icon: "warning",
          title: "Provisioning Pending",
          text: "All four databases must be READY before activation.",
          confirmButtonColor: "#059669"
        });
      }
    }

    setSavingLicense(true);

    try {
      const response = await api.post("/api/license/push", {
        tenantId: Number(tenantId),
        planCode: license.planCode,
        maxUsers: Number(license.maxUsers),
        expiresOn: license.expiresOn,
        graceDays: Number(license.graceDays),
        isSuspended: license.isSuspended,
        featuresJson: license.featuresJson.trim() || null,
        actor: "ui"
      });

      if (response.data?.success === false || response.data?.ok === false) {
        throw new Error(response.data?.message || "License update failed.");
      }

      await Swal.fire({
        icon: "success",
        title: "License Updated",
        confirmButtonColor: "#059669"
      });

      await loadDetails();
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "License Update Failed",
        text: getErrorMessage(err),
        confirmButtonColor: "#059669"
      });
    } finally {
      setSavingLicense(false);
    }
  };

  // ==========================================================
  // ACTIVATE / SUSPEND TENANT
  // ==========================================================

  const handleToggleSuspend = async () => {
    if (toggling || isProvisioningBusy) return;

    if (license.isSuspended) {
      const status = await loadProvisioningStatus();

      if (!status || status.allReady !== true) {
        return Swal.fire({
          icon: "warning",
          title: "Provisioning Pending",
          text: "All four databases must be READY before activation.",
          confirmButtonColor: "#059669"
        });
      }
    }

    const confirmation = await Swal.fire({
      title: license.isSuspended ? "Activate Tenant?" : "Suspend Tenant?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#059669"
    });

    if (!confirmation.isConfirmed) return;

    setToggling(true);

    try {
      const response = await api.post("/api/license/toggle-suspend", {
        tenantId: Number(tenantId),
        actor: "ui"
      });

      if (response.data?.success === false || response.data?.ok === false) {
        throw new Error(response.data?.message || "Status update failed.");
      }

      await loadDetails();

      await Swal.fire({
        icon: "success",
        title: "Status Updated",
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Status Update Failed",
        text: getErrorMessage(err)
      });
    } finally {
      setToggling(false);
    }
  };

  const refreshAll = () => loadDetails();

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="flex min-h-screen flex-col md:flex-row">
        <SuperAdminNav />

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600 text-white">
                <Building2 size={22} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Edit Tenant</h1>
                <p className="text-sm text-slate-500">
                  {tenant?.TenantCode || "Loading tenant..."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Time />
              <button
                type="button"
                onClick={() => navigate("/supremeadmin/tenant")}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <ArrowLeft size={16} /> Back
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 rounded-xl bg-white py-20 text-slate-500">
              <Loader2 className="animate-spin" />
              Loading tenant information...
            </div>
          ) : error ? (
            <div className="rounded-xl border border-rose-200 bg-white p-6">
              <p className="mb-4 text-sm text-rose-700">{error}</p>
              <button
                type="button"
                onClick={loadDetails}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm text-white"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {/* TENANT SUMMARY */}
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
                <div>
                  <p className="text-sm font-bold text-slate-900">
                    {tenant?.Name}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Tenant ID: {tenant?.TenantId} · Code: {tenant?.TenantCode}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={refreshAll}
                  disabled={loading || provisioningLoading}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:opacity-50"
                >
                  <RefreshCw size={16} /> Refresh
                </button>
              </div>

              {/* COMPANY INFORMATION */}
              <form onSubmit={handleCompanySave} className="space-y-5">
                <Section title="Company Information" icon={Building2}>
                  <Field
                    label="Company Name"
                    value={company.name}
                    onChange={(v) => setCompanyField("name", v)}
                    required
                    maxLength={200}
                  />
                  <Field
                    label="Legal Company Name"
                    value={company.legalName}
                    onChange={(v) => setCompanyField("legalName", v)}
                    required
                    maxLength={200}
                  />
                  <Field
                    label="Domain"
                    value={company.domain}
                    onChange={(v) => setCompanyField("domain", v)}
                  />
                  <Field
                    label="Tenant Code (Read Only)"
                    value={tenant?.TenantCode}
                    onChange={() => {}}
                    disabled
                  />
                </Section>

                <Section title="Contact Information" icon={UserRound}>
                  {[
                    ["contactPerson", "Contact Person"],
                    ["email", "Email"],
                    ["phone", "Phone"],
                    ["alternatePhone", "Alternate Phone"],
                    ["website", "Website"]
                  ].map(([key, label]) => (
                    <Field
                      key={key}
                      label={label}
                      value={company[key]}
                      onChange={(v) => setCompanyField(key, v)}
                      type={key === "email" ? "email" : "text"}
                    />
                  ))}
                </Section>

                <Section title="Company Address" icon={MapPin}>
                  {[
                    ["addressLine1", "Address Line 1"],
                    ["addressLine2", "Address Line 2"],
                    ["city", "City"],
                    ["stateName", "State"],
                    ["postalCode", "PIN Code"],
                    ["country", "Country"]
                  ].map(([key, label]) => (
                    <Field
                      key={key}
                      label={label}
                      value={company[key]}
                      onChange={(v) => setCompanyField(key, v)}
                    />
                  ))}
                </Section>

                <Section title="Tax Information" icon={FileText}>
                  <Field
                    label="GSTIN"
                    value={company.gstin}
                    onChange={(v) => setCompanyField("gstin", v.toUpperCase())}
                    maxLength={15}
                  />
                  <Field
                    label="PAN"
                    value={company.pan}
                    onChange={(v) => setCompanyField("pan", v.toUpperCase())}
                    maxLength={10}
                  />
                </Section>

                <div className="flex justify-end rounded-xl border border-slate-200 bg-white p-4">
                  <button
                    type="submit"
                    disabled={savingCompany}
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                  >
                    {savingCompany
                      ? <Loader2 size={17} className="animate-spin" />
                      : <Save size={17} />}
                    {savingCompany ? "Saving..." : "Update Company"}
                  </button>
                </div>
              </form>

              {/* DATABASE PROVISIONING */}
              <Section
                title="Database Provisioning"
                icon={Database}
                action={
                  <button
                    type="button"
                    onClick={() => loadProvisioningStatus()}
                    disabled={provisioningLoading}
                    className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 disabled:opacity-50"
                  >
                    <RefreshCw
                      size={14}
                      className={provisioningLoading ? "animate-spin" : ""}
                    />
                    Refresh Status
                  </button>
                }
              >
                <div className="grid grid-cols-2 gap-3 md:col-span-2 sm:grid-cols-4">
                  {[
                    { label: "Total", value: databases.length, color: "text-slate-800" },
                    { label: "Ready", value: readyCount, color: "text-emerald-700" },
                    { label: "Running", value: runningCount, color: "text-blue-700" },
                    { label: "Failed", value: failedCount, color: "text-rose-700" }
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-lg border border-slate-200 bg-slate-50 p-3"
                    >
                      <p className="text-xs text-slate-500">{item.label}</p>
                      <p className={`mt-1 text-xl font-bold ${item.color}`}>
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2 md:col-span-2">
                  <StatusBadge
                    status={provisioningEnabled ? "ENABLED" : "DISABLED"}
                  />
                  <span className="rounded-full border border-slate-200 px-3 py-1 text-xs">
                    Templates: {templatesApproved ? "Approved" : "Not approved"}
                  </span>
                  {isProvisioningBusy && <StatusBadge status="RUNNING" />}
                </div>

                {provisioningError && (
                  <div className="md:col-span-2">
                    <Notice type="error">{provisioningError}</Notice>
                  </div>
                )}

                {provisioningResult && (
                  <div className="md:col-span-2">
                    <Notice type="success">{provisioningResult}</Notice>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-3 md:col-span-2 lg:grid-cols-2">
                  {DB_TYPES.map((type) => {
                    const item = databases.find(
                      (db) => db.DatabaseType === type
                    );
                    return (
                      <div
                        key={type}
                        className="rounded-lg border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="mb-3 flex items-center justify-between gap-2">
                          <span className="text-xs font-bold text-emerald-700">
                            {type}
                          </span>
                          <StatusBadge
                            status={item?.ProvisioningStatus || "NOT CONFIGURED"}
                          />
                        </div>
                        <p className="break-all font-mono text-xs text-slate-800">
                          {item?.DatabaseName || "Not configured"}
                        </p>
                        <p className="mt-2 text-xs text-slate-500">
                          Financial Year: {item?.FinancialYear || "Not applicable"}
                        </p>
                      </div>
                    );
                  })}
                </div>

                <div className="md:col-span-2">
                  {allDatabasesReady ? (
                    <Notice type="success">
                      All four database records are marked READY.
                      Verify database integrity, tenant-data cleanliness and
                      configuration before activating the tenant.
                    </Notice>
                  ) : isProvisioningBusy ? (
                    <Notice type="info">
                      Database provisioning is running.
                      Completed: {readyCount} of 4 databases.
                      Status refreshes approximately every 5 seconds.
                    </Notice>
                  ) : (
                    <Notice type="warning">
                      Database provisioning is incomplete.
                      {` ${readyCount} of 4 databases are READY.`}
                    </Notice>
                  )}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 md:col-span-2">
                  <div className="flex items-start gap-2">
                    <Info size={18} className="mt-0.5 shrink-0 text-blue-600" />
                    <div>
                      <p className="text-sm font-semibold text-slate-800">
                        Database Restoration
                      </p>
                      <p className="mt-1 max-w-xl text-xs text-slate-500">
                        Provision the MASTER, INVENTORY, PAYROLL and TALLY
                        databases from approved SQL Server backups.
                        Existing databases will not be overwritten.
                      </p>
                      {!provisioningEnabled && (
                        <p className="mt-2 text-xs text-amber-700">
                          Backend provisioning is disabled.
                        </p>
                      )}
                      {provisioningEnabled && !templatesApproved && (
                        <p className="mt-2 text-xs text-amber-700">
                          Backup templates require approval before restoration.
                        </p>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleStartProvisioning}
                    disabled={!canStartProvisioning}
                    className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition ${
                      canStartProvisioning
                        ? "bg-emerald-600 hover:bg-emerald-700"
                        : "cursor-not-allowed bg-slate-300 text-slate-600"
                    }`}
                  >
                    {isProvisioningBusy
                      ? <Loader2 size={16} className="animate-spin" />
                      : <Play size={16} />}
                    {isProvisioningBusy
                      ? "Provisioning..."
                      : allDatabasesReady
                        ? "All Databases Ready"
                        : "Start Provisioning"}
                  </button>
                </div>
              </Section>

              {/* JOB HISTORY */}
              <Section
                title="Provisioning Job History"
                icon={History}
                action={
                  <button
                    type="button"
                    onClick={() => loadProvisioningStatus()}
                    disabled={provisioningLoading}
                    className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 disabled:opacity-50"
                  >
                    <RefreshCw size={14} />
                    Refresh
                  </button>
                }
              >
                <div className="min-w-0 md:col-span-2">
                  {provisioningJobs.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
                      <History
                        size={28}
                        className="mx-auto mb-2 text-slate-400"
                      />
                      <p className="text-sm font-semibold text-slate-700">
                        No Provisioning Jobs
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        No database restore jobs have been recorded.
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                      <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
                        <thead className="bg-emerald-700 text-white">
                          <tr>
                            {[
                              "Database", "Status", "Stage", "Message",
                              "Created", "Started", "Ended"
                            ].map((heading) => (
                              <th
                                key={heading}
                                className="whitespace-nowrap px-3 py-3 font-semibold"
                              >
                                {heading}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {provisioningJobs.map((job, index) => (
                            <tr
                              key={job.JobId || index}
                              className="hover:bg-slate-50"
                            >
                              <td className="max-w-56 break-all px-3 py-3 font-mono text-slate-700">
                                {job.TargetDbName || "-"}
                              </td>
                              <td className="px-3 py-3">
                                <JobStatusBadge status={job.Status} />
                              </td>
                              <td className="px-3 py-3 text-slate-700">
                                {job.Stage || "-"}
                              </td>
                              <td className="max-w-72 break-words px-3 py-3 text-slate-600">
                                {job.Message || "-"}
                              </td>
                              <td className="whitespace-nowrap px-3 py-3 text-slate-600">
                                {formatDate(job.CreatedAt)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-3 text-slate-600">
                                {formatDate(job.StartedAt)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-3 text-slate-600">
                                {formatDate(job.EndedAt)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </Section>

              {/* LICENSE SETTINGS */}
              <form onSubmit={handleLicenseSave} className="space-y-5">
                <Section title="License Settings" icon={ShieldCheck}>
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-slate-700">
                      License Plan
                    </span>
                    <select
                      value={license.planCode}
                      onChange={(e) =>
                        setLicenseField("planCode", e.target.value)
                      }
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm"
                    >
                      <option value="STARTER">STARTER</option>
                      <option value="PRO">PRO</option>
                      <option value="ENTERPRISE">ENTERPRISE</option>
                    </select>
                  </label>

                  <Field
                    label="Maximum Users"
                    type="number"
                    min={1}
                    required
                    value={license.maxUsers}
                    onChange={(v) => setLicenseField("maxUsers", v)}
                  />

                  <Field
                    label="Expiry Date"
                    type="date"
                    required
                    value={license.expiresOn}
                    onChange={(v) => setLicenseField("expiresOn", v)}
                  />

                  <Field
                    label="Grace Days"
                    type="number"
                    min={0}
                    max={3650}
                    value={license.graceDays}
                    onChange={(v) => setLicenseField("graceDays", v)}
                  />

                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-slate-700">
                      Suspended
                    </span>
                    <select
                      value={license.isSuspended ? "1" : "0"}
                      onChange={(e) =>
                        setLicenseField("isSuspended", e.target.value === "1")
                      }
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm"
                    >
                      <option value="1">Yes</option>
                      <option value="0" disabled={!allDatabasesReady}>
                        No
                      </option>
                    </select>
                  </label>

                  <div className="md:col-span-2">
                    <label className="block">
                      <span className="mb-1.5 block text-sm font-medium text-slate-700">
                        Features JSON
                      </span>
                      <textarea
                        rows={5}
                        value={license.featuresJson}
                        onChange={(e) =>
                          setLicenseField("featuresJson", e.target.value)
                        }
                        className="w-full rounded-lg border border-slate-300 p-3 font-mono text-xs"
                      />
                    </label>
                  </div>
                </Section>

                <div className="flex flex-wrap items-center justify-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
                  <button
                    type="button"
                    onClick={handleToggleSuspend}
                    disabled={
                      toggling ||
                      savingLicense ||
                      isProvisioningBusy ||
                      (license.isSuspended && !allDatabasesReady)
                    }
                    className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60 ${
                      license.isSuspended
                        ? "bg-emerald-600 hover:bg-emerald-700"
                        : "bg-rose-600 hover:bg-rose-700"
                    }`}
                  >
                    {license.isSuspended
                      ? <CheckCircle2 size={16} />
                      : <Ban size={16} />}
                    {toggling
                      ? "Updating..."
                      : license.isSuspended
                        ? "Activate Tenant"
                        : "Suspend Tenant"}
                  </button>

                  <button
                    type="submit"
                    disabled={
                      savingLicense ||
                      toggling ||
                      isProvisioningBusy ||
                      (!license.isSuspended && !allDatabasesReady)
                    }
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {savingLicense
                      ? <Loader2 size={17} className="animate-spin" />
                      : <Save size={17} />}
                    {savingLicense ? "Saving..." : "Update License"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
